import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, ParamMap, Params, UrlSegment } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import storage from 'local-storage-fallback';
import { MEMBER_ACCESS_TOKEN_QUERY_PARAM } from '@app/shared/const';
import { Meta } from '@angular/platform-browser';
import {
  AUTOLOGIN_ENCODED_ID_QUERY_PARAM,
  AUTOLOGIN_ENCODED_MSISDN_QUERY_PARAM,
  LOYALTY_CLUB_METADATA_SELECTOR,
  SOURCE_QUERY_PARAM,
} from '@app/core/consts';

const FORCE_OPEN_STORE_WHEN_MOBILE_KEY = 'open_store_when_mobile';

const getParamValueFromRoute = (paramName: string, route: ActivatedRouteSnapshot): string | null =>
  route.paramMap.get(paramName) || route.queryParamMap.get(paramName);
const parseOidcLanguages = (paramValue: string | null): string[] | null =>
  paramValue ? paramValue.split(' ') : null;
const OIDC_LANGUAGES_PARAM = 'ui_locales';
const OIDC_LOGIN_HINT = 'login_hint';

const changeToRouteCommands = (segment: UrlSegment): (string | Record<string, string>)[] => {
  let result: (string | Record<string, string>)[] = [
    segment.path,
  ];
  const parameters = segment.parameters;
  if (parameters && Object.keys(parameters).length > 0) {
    result = [
      ...result,
      parameters,
    ];
  }
  return result;

};

@Injectable({ providedIn: 'root' })
export class BaseParamsService {
  public static readonly DEFAULT_WEBFORMS_PRODUCT = 'webforms';
  public slug: string;
  public source: string;
  public subsource: string | null;
  public paramsLanguage: string | null;
  public memberAccessToken: string | null;
  public oidcLanguages: string[] | null = null;
  public oidcLoginHint: string | null = null;

  public set language(language: string) {
    this.language$.next(language);
  }

  public get language(): string | undefined {
    return this.language$.value;
  }

  constructor(private meta: Meta) {}

  public language$ = new BehaviorSubject<string | undefined>(undefined);
  public forceOpenStoreWhenMobile: boolean | null;
  private autoLoginEncodedMsisdn: string | null | undefined;
  private autoLoginEncodedId: string | null | undefined;
  private paramsResolvedSubject = new BehaviorSubject<boolean | null>(null);
  private rootQUrlSegment: UrlSegment | undefined;

  public hasAutologinParams(): boolean {
    return !!this.autoLoginEncodedMsisdn || !!this.autoLoginEncodedId;
  }

  public parseParams(route: ActivatedRouteSnapshot): boolean {
    let updated = false;
    if (!this.paramsResolvedSubject.getValue()) {
      this.slug = this.fetchSlug();
      const getParamValue = (paramName: string): string | null => getParamValueFromRoute(paramName, route);
      this.source = getParamValue(SOURCE_QUERY_PARAM) || BaseParamsService.DEFAULT_WEBFORMS_PRODUCT;
      this.subsource = getParamValue('subsource');
      this.paramsLanguage = getParamValue('lang');
      this.memberAccessToken = getParamValue(MEMBER_ACCESS_TOKEN_QUERY_PARAM) || null;
      this.oidcLanguages = parseOidcLanguages(getParamValue(OIDC_LANGUAGES_PARAM));
      this.oidcLoginHint = getParamValue(OIDC_LOGIN_HINT);

      if (route.url[0]?.path === 'q') {
        this.rootQUrlSegment = route.url[0];
      } else {
        this.rootQUrlSegment = undefined;
      }

      if (route.queryParamMap.has(FORCE_OPEN_STORE_WHEN_MOBILE_KEY)) {
        this.forceOpenStoreWhenMobile = route.queryParamMap.get(FORCE_OPEN_STORE_WHEN_MOBILE_KEY) === 'true';
      }
      updated = true;
    }

    this.paramsResolvedSubject.next(true);
    return updated;
  }

  // autologin description https://placewisegroup.slack.com/archives/C07Q89D0MK5/p1741963531193769
  public parseParamsWithAutologgable(route: ActivatedRouteSnapshot): void {
    this.parseParams(route);
    this.parseAutologinParams(route.queryParamMap);
  }

  public getAuthToken(): string | null {
    return storage.getItem(this.authTokenKey());
  }

  public authTokenKey(): string {
    return `s-${this.slug}`;
  }

  public cookieConsentKey(): string {
    return `cookie-consent-${this.slug}`;
  }

  public otpClientSecretKey(): string {
    return `otp-cs-${this.slug}`;
  }

  public getAuthHeaders(): HttpHeaders {
    const authToken = this.getAuthToken() || '';
    const headers: Record<string, string> = authToken ? { 'X-AuthToken': authToken } : {};
    return new HttpHeaders(headers);
  }

  public getAuthHeadersWithAutologin(includeNormalAuthToken = true): HttpHeaders | null {
    if (this.autoLoginEncodedMsisdn) { return this.getAutlogoinEncodedMsisdnAuthHeaders(); }
    if (this.autoLoginEncodedId) { return this.getAutoLoginEncodedIdAuthHeaders(); }
    if (!!this.getAuthToken() && includeNormalAuthToken) { return this.getAuthHeaders(); }
    return null;
  }

  public clearAutologinParams(): void {
    this.autoLoginEncodedMsisdn = null;
    this.autoLoginEncodedId = null;
  }

  public getParamsResolvedObservable(): Observable<boolean> {
    return this.paramsResolvedSubject.asObservable()
      .pipe(
        filter(Boolean),
        first(),
      );
  }

  public getRootURI(parameters?: Params): string {
    const urlSegment = this.getQUrlSegment(parameters);

    return urlSegment ? `/${urlSegment.toString()}` : '/';
  }

  /**
   * Formatted for use with `router.createUrlTree`
   */
  public getRootURIUrlCommands(parameters?: Params): (string | Record<string, string>)[] {
    const urlSegment = this.getQUrlSegment(parameters);

    return urlSegment ? ['/', ...changeToRouteCommands(urlSegment)] : ['/'];
  }

  public getQUrlSegment(parameters?: Params): UrlSegment | undefined {
    if (!this.rootQUrlSegment) {
      return undefined;
    }

    return parameters
      ? new UrlSegment(this.rootQUrlSegment.path, { ...this.rootQUrlSegment.parameters, ...parameters })
      : this.rootQUrlSegment;
  }

  public isInsideMobileAppWithToken(): boolean {
    return !!this.memberAccessToken && this.isInsideMobileApp();
  }

  public isInsideMobileApp(): boolean {
    return this.source === 'android' || this.source === 'ios';
  }

  private parseAutologinParams(queryParamMap: ParamMap): void {
    this.autoLoginEncodedMsisdn = queryParamMap.get(AUTOLOGIN_ENCODED_MSISDN_QUERY_PARAM) || this.autoLoginEncodedMsisdn;
    this.autoLoginEncodedId = queryParamMap.get(AUTOLOGIN_ENCODED_ID_QUERY_PARAM) || this.autoLoginEncodedId;
  }

  private getAutlogoinEncodedMsisdnAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ 'X-Encoded': this.autoLoginEncodedMsisdn || '' });
  }

  private getAutoLoginEncodedIdAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ 'X-ID-Encoded': this.autoLoginEncodedId || '' });
  }

  private fetchSlug(): string {
    return this.meta.getTag(LOYALTY_CLUB_METADATA_SELECTOR)?.content || '';
  }
}
